/**
 * Client-specific types used primarily in the web dashboard.
 * These types are specific to the client application but may be used elsewhere.
 */

import { BaseEntity, DateField } from './base';

// ============================================================================
// CONFIGURATION
// ============================================================================

export interface Configuration {
  isInquiryEnabled?: boolean;
  isFL324PExportEnabled?: boolean;
  isInvoicingEnabled?: boolean;
  isChatEnabled?: boolean;
}

// ============================================================================
// INQUIRY
// ============================================================================

export enum InquiryStatus {
  PENDING = "pending",
  COMPLETED = "completed",
  CLOSED = "closed",
}

export interface Inquiry extends BaseEntity {
  time: string;
  hoursPerWeekDetails: string;
  hourlyRate: number;
  travelRate: number;
  accountId: string;
  dateOfContact: DateField;
  status: InquiryStatus;
  identifier: string;
  location: string;
  parents: InquiryParent[];
  children: InquiryChild[];
  courtOrder: boolean;
  hoursPerWeek?: number;
  restrainingOrder: boolean;
  notes?: string;
  caseId?: string;
}

export interface InquiryParent extends BaseEntity {
  firstName: string;
  lastName: string;
  role?: string;
  phoneNumber: string;
  email: string;
  dob?: DateField;
  relationshipToChild: string;
  feePercentage?: number;
  custodyStatus: string;
  mentalHealthConcerns?: boolean;
  isCurrentlyReceivingTherapy?: boolean;
  historyOfSubstanceAbuse?: boolean;
  notes?: string;
  accountId?: string;
  inquiryId?: string;
  attorneyName?: string;
  initiatedContact?: boolean;
}

export interface InquiryChild extends BaseEntity {
  firstName: string;
  lastName: string;
  age?: string;
  accountId?: string;
  inquiryId?: string;
  createdBy?: string;
  updatedBy?: string;
  hasSpecialNeeds?: boolean;
  allergies?: string;
  notes?: string;
}

// ============================================================================
// DOCUMENT TEMPLATE
// ============================================================================

export interface DocumentTemplate extends BaseEntity {
  title?: string;
  castEid?: string;
  accountId?: string;
  isActive?: boolean;
}

export enum ClientDocumentType {
  SecurityProcedures = "security_procedures",
  ServiceAgreement = "service_agreement",
  PaymentAgreement = "payment_agreement",
}

export enum ClientDocumentStatus {
  Draft = "draft",
  Active = "active",
  Archived = "archived",
}

export interface ClientDocument extends BaseEntity {
  type?: ClientDocumentType;
  status?: ClientDocumentStatus;
  createdBy: string;
  accountId: string;
  parentId: string;
  documentTemplateId: string;
}

// ============================================================================
// PRODUCT (Stripe)
// ============================================================================

export interface Product extends BaseEntity {
  name: string;
  description?: string;
  active?: boolean;
  metadata?: Record<string, string>;
}

export interface ProductPrice extends BaseEntity {
  unit_amount: number;
  currency?: string;
  recurring?: {
    interval: 'day' | 'week' | 'month' | 'year';
    interval_count: number;
  };
  productId?: string;
}

// ============================================================================
// CLIENT SETTINGS
// ============================================================================

export interface ClientSetting extends BaseEntity {
  defaultVisitationPrice: number;
  defaultVisitNotes: string;
  isAIEnabled: boolean;
}

// ============================================================================
// FILE UPLOAD
// ============================================================================

export type FileCategory = "contracts" | "court_paperwork" | "reports" | "misc";

export const FILE_CATEGORY_LABELS: Record<FileCategory, string> = {
  contracts: "Contracts",
  court_paperwork: "Court Paperwork",
  reports: "Reports",
  misc: "Miscellaneous",
};

export const FILE_CATEGORY_ORDER: FileCategory[] = [
  "contracts",
  "court_paperwork",
  "reports",
  "misc",
];

export interface ClientFile extends BaseEntity {
  name?: string;
  description?: string;
  path: string;
  createdBy: string;
  caseId?: string;
  isVisibleToSupervisor: boolean;
  size?: number;
  type?: string;
  category?: FileCategory;
}

export interface EditableFile {
  file: File;
  name: string;
  description?: string;
  preview: string | null;
}

// ============================================================================
// FILTER
// ============================================================================

export interface Filter {
  supervisorId: string | null | undefined;
  caseId: string | null | undefined;
}

// ============================================================================
// INTEGRATION
// ============================================================================

export interface Integration extends BaseEntity {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: DateField;
  provider?: string;
}

// ============================================================================
// ONBOARDING
// ============================================================================

export interface Onboarding extends BaseEntity {
  showVisitsBanner: boolean;
  showCasesBanner: boolean;
  showSupervisorsBanner: boolean;
  showParentsBanner: boolean;
  showChildrenBanner: boolean;
  showReportsBanner: boolean;
  showLocationsBanner: boolean;
  showChatBanner: boolean;
  showContractsBanner: boolean;
  showFilesBanner: boolean;
  showContactsBanner: boolean;
  lastUpdated: DateField;
}

